'use client';

/**
 * ObeyaPlanLensRoomsCard — the room pivot as a morphological box: one group
 * per room type, the room's requirements down the rows and the benchmark
 * tiers across (the columns, their order, every mark and every span arrive
 * from the BFF). A pick is a filled dot; candidates are hollow dots joined
 * by a line. Same-label requirements are separate rows; a requirement
 * serving other rooms says so. Nothing is imputed or derived here.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaPlanLensRoomsCard
 * @see klyde_edge/klyde_intelligence/projects/plan_rooms.py
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo, useState } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { money, TIER_COLOR } from './use-plan-lenses';
import { PlanLensFrame } from './plan-lens-frame';
import { usePlanRooms } from './use-plan-rooms';

// How a shipped span position is drawn (presentation only).
const SPAN_LINE: Record<string, { left: string; right: string }> = {
  start: { left: '50%', right: '0' },
  middle: { left: '0', right: '0' },
  end: { left: '0', right: '50%' },
};

const GRID = 'grid grid-cols-[minmax(220px,1.4fr)_60px_repeat(3,72px)_minmax(180px,1.2fr)_minmax(140px,1fr)] gap-x-3';

export function ObeyaPlanLensRoomsCard() {
  const { contentKey } = useCardIdentity();
  const { mcpId, strategy, d, isLoading, queryKey } = usePlanRooms();
  const [picked, setPicked] = useState<string | null>(null);

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_lens_rooms',
    status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: queryKey,
    text: d ? d.labels.rooms_title : 'Rooms as a grid',
    json: d ? d.rooms : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, d, mcpId, strategy]));

  if (!mcpId) return null;
  if (isLoading && !d) return <div className={cn(text.muted, 'p-6 text-center')}>…</div>;
  if (!d) return null;

  const L = d.labels;
  const data = d.rooms.data;
  const room = data.rooms.find((r) => r.room === picked) ?? data.rooms[0];
  if (!room) return null;

  return (
    <PlanLensFrame title={L.rooms_title} meta={L.rooms_meta}>
      <div className="flex flex-col gap-4">

      <div className={cn(text.muted)}>
        <b className="text-foreground tabular-nums">{data.requirement_count}</b> {L.requirements_word} · <b className="text-foreground tabular-nums">{data.requirement_in_room_lines}</b> {L.room_lines}
      </div>

      <div className="flex gap-2 flex-wrap">
        {data.rooms.map((r) => (
          <button
            key={r.room}
            type="button"
            onClick={() => setPicked(r.room)}
            className={cn(
              text.muted, 'px-3 py-1 rounded-md border',
              r.room === room.room ? 'border-[#7fa2ff] bg-[#7fa2ff]/10 text-foreground font-semibold' : 'border-border',
            )}
          >
            {r.room}
          </button>
        ))}
      </div>

      <div className={cn(text.muted, 'flex gap-5 flex-wrap')}>
        {room.real_room_count != null && <span><b className="text-foreground tabular-nums">{room.real_room_count}</b> {L.rooms_word}</span>}
        <span>{L.one_room_at}</span>
        {data.tier_columns.map((t) => (
          <span key={t}>
            <span className="inline-block w-[7px] h-[7px] rounded-full mr-1.5" style={{ background: TIER_COLOR[t] }} />
            {L[`tier_${t}`]} <b className="text-foreground tabular-nums">{room.one_room_at_tier[t] != null ? money(room.one_room_at_tier[t]!) : L.mark_none}</b>
          </span>
        ))}
        <span>{L.state_selected} <b className="text-foreground tabular-nums">{room.selected_count}</b> · {L.state_open} <b className="text-foreground tabular-nums">{room.open_count}</b></span>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[760px]">
          <div className={cn(text.eyebrow, GRID, 'pb-1 border-b border-border')}>
            <span>{L.col_requirement}</span>
            <span className="text-right">{L.col_qty}</span>
            {data.tier_columns.map((t) => <span key={t} className="text-center">{L[`col_tier_${t}`]}</span>)}
            <span>{L.col_pick}</span>
            <span className="text-right">{L.col_candidates}</span>
          </div>
          {room.lines.map((line) => (
            <div key={line.requirement_id} className={cn(GRID, 'items-center py-1 border-b border-border/50', line.state === 'open' && 'opacity-70')}>
              <div className="min-w-0">
                <div className={cn(text.body, 'truncate', line.state === 'selected' && 'font-semibold')}>{line.requirement ?? L.unlabelled}</div>
                {line.also_serves.length > 0 && <div className={cn(text.muted, 'truncate')}>{L.also_serves} {line.also_serves.join(', ')}</div>}
              </div>
              <span className={cn(text.muted, 'text-right tabular-nums')}>{line.quantity_in_room ?? L.mark_none}</span>
              {data.tier_columns.map((t) => {
                const cell = line.tier_cells[t];
                const seg = SPAN_LINE[cell.span];
                return (
                  <div key={t} className="relative h-6 flex items-center justify-center">
                    {seg && <span className="absolute h-[3px] rounded opacity-50" style={{ background: TIER_COLOR[t], left: seg.left, right: seg.right }} />}
                    {cell.mark === 'pick' && <span className="relative w-[13px] h-[13px] rounded-full ring-2 ring-foreground" style={{ background: TIER_COLOR[t] }} />}
                    {cell.mark === 'candidate' && <span className="relative w-[9px] h-[9px] rounded-full border-2 bg-background" style={{ borderColor: TIER_COLOR[t] }} />}
                  </div>
                );
              })}
              <div className="min-w-0">
                {line.pick ? (
                  <>
                    <div className={cn(text.body, 'truncate')}>{line.pick.name}</div>
                    <div className={cn(text.muted, 'truncate tabular-nums')}>{line.pick.vendor} · {line.pick.unit_price != null ? money(line.pick.unit_price) : L.mark_none}{line.quoted ? ` · ${L.quoted}` : ''}</div>
                  </>
                ) : (
                  <span className={cn(text.muted)}>{L.state_open}{line.quoted ? ` · ${L.quoted}` : ''}</span>
                )}
              </div>
              <span className={cn(text.muted, 'text-right tabular-nums')}>
                {line.candidate_count}{line.unit_price_range ? ` · ${money(line.unit_price_range.low)}–${money(line.unit_price_range.high)}` : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
      </div>
    </PlanLensFrame>
  );
}
